import React, { useEffect, useState } from 'react';
import { ArrowRight, Flame, Layers } from 'lucide-react';
import { ProgressBar, cn } from '../../../ui';
import { fetchContinueLearning } from '../../../../utils/smartLearning';
import TileLink from '../shared/TileLink';
import { clampPct } from '../studentHomeRules';

const SessionTile = ({ card }) => {
  const live = card.session?.status === 'live';
  const percent = clampPct(card.overallPercent);
  return (
    <TileLink
      to="/s/learning"
      state={{ preselectCourseId: card.course._id }}
      className={cn(
        'tw-flex tw-min-w-0 tw-flex-col tw-rounded tw-border tw-border-solid tw-bg-surface-raised tw-px-3.5 tw-py-3',
        live ? 'tw-border-danger/40 hover:tw-border-danger/60' : 'tw-border-line hover:tw-border-primary/45',
      )}
    >
      <div className="tw-mb-1 tw-flex tw-items-center tw-justify-between tw-gap-2">
        <span className="tw-min-w-0 tw-truncate tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-content-subtle">
          {card.course.name}
        </span>
        {live ? (
          <span className="tw-inline-flex tw-shrink-0 tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-danger tw-px-2 tw-py-px tw-text-[0.625rem] tw-font-medium tw-uppercase tw-tracking-caps tw-text-on-status">
            <span className="tw-h-[5px] tw-w-[5px] tw-rounded-full tw-bg-on-status tw-animate-live-pulse motion-reduce:tw-animate-none" aria-hidden="true" />
            Live
          </span>
        ) : (
          <span className="tw-shrink-0 tw-text-[0.6875rem] tw-font-medium tw-text-primary">{percent}%</span>
        )}
      </div>
      <h3 className="tw-m-0 tw-text-[0.9375rem] tw-font-medium tw-leading-snug tw-text-content">{card.session.title}</h3>
      {card.session.moduleTitle && (
        <p className="tw-m-0 tw-mt-1 tw-flex tw-items-center tw-gap-1 tw-text-[0.6875rem] tw-text-content-subtle">
          <Layers size={11} aria-hidden="true" className="tw-shrink-0" />
          <span className="tw-min-w-0 tw-truncate">Module {card.session.moduleNumber}: {card.session.moduleTitle}</span>
        </p>
      )}
      <div className="tw-mt-auto tw-pt-2.5">
        {!live && <ProgressBar value={percent} size="sm" color="primary" className="tw-mb-2" />}
        <span className={cn('tw-inline-flex tw-items-center tw-gap-1 tw-text-xs tw-font-semibold', live ? 'tw-text-danger-fg' : 'tw-text-primary')}>
          {live ? 'Join live class' : 'Resume session'}
          <ArrowRight size={14} aria-hidden="true" />
        </span>
      </div>
    </TileLink>
  );
};

/** "Pick up where you left off", for courses on Smart Learning. Nothing at all otherwise. */
const ContinueLearning = () => {
  const [cards, setCards] = useState([]);

  useEffect(() => {
    let cancelled = false;
    fetchContinueLearning().then((rows) => { if (!cancelled) setCards(rows); });
    return () => { cancelled = true; };
  }, []);

  if (cards.length === 0) return null;

  return (
    <section
      aria-label="Continue learning"
      className="tw-rounded-lg tw-border tw-border-solid tw-border-line tw-bg-surface-raised tw-bg-[radial-gradient(800px_circle_at_0%_0%,hsl(var(--primary)/0.08),transparent_50%)] tw-p-4 tw-shadow-card page-sm:tw-px-5"
    >
      <div className="tw-mb-3 tw-flex tw-flex-wrap tw-items-center tw-gap-2">
        <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-rounded-full tw-bg-primary-soft tw-px-2.5 tw-py-1 tw-text-[0.6875rem] tw-font-semibold tw-uppercase tw-tracking-caps tw-text-primary-soft-fg">
          <Flame size={12} aria-hidden="true" />
          Continue learning
        </span>
        <span className="tw-text-xs tw-text-content-subtle">Pick up where you left off</span>
      </div>
      <div className="tw-grid tw-grid-cols-[repeat(auto-fit,minmax(min(100%,240px),1fr))] tw-gap-2.5">
        {cards.map((card) => <SessionTile key={`${card.course._id}-${card.session._id}`} card={card} />)}
      </div>
    </section>
  );
};

export default ContinueLearning;
